import {
  Menu,
  MenuDivider,
  MenuHeader,
  MenuItem,
  MenuRadioGroup,
  RadioChangeEvent,
  SubMenu,
} from "@szhsin/react-menu";
import { Fragment, ReactNode } from "react";

import Button, { ButtonSize, buttonTheme } from "@components/button/button";
import { toObjectProps, useLink } from "@components/link/link";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { titleCase } from "@helpers/string";

export type DropdownOptionButtonType = {
  type?: "button";
  disabled?: boolean;
  label: string | ReactNode;
  description?: string | ReactNode;
  onClick: (data: { value?: any; syntheticEvent: any }) => void;
  textColor?: string;
  className?: string;
  labelClassName?: string;
};
export type DropdownOptionLinkType = {
  type: "link";
  disabled?: boolean;
  label: string | ReactNode;
  description?: string | ReactNode;
  to: string | toObjectProps;
  textColor?: string;
  className?: string;
  labelClassName?: string;
};
export type DropdownOptionDividerType = {
  type: "divider";
};
export type DropdownOptionHeaderType = {
  type: "header";
  label: string;
};
export type DropdownOptionRadioType = {
  type: "radio";
  label: string;
  value: boolean | string | number;
  onChangeValue: (e: RadioChangeEvent) => void;
  options: {
    label: string;
    disabled?: boolean;
    value: boolean | string | number;
  }[];
};
export type DropdownOptionSubMenuType = {
  type: "submenu";
  label: string;
  options: DropdownOptionType[];
};

export type DropdownOptionType =
  | DropdownOptionButtonType
  | DropdownOptionRadioType
  | DropdownOptionLinkType
  | DropdownOptionHeaderType
  | DropdownOptionSubMenuType
  | DropdownOptionDividerType;

export const removeConsecutiveDividers = (
  arr: DropdownOptionType[]
): DropdownOptionType[] => {
  return arr.reduce<DropdownOptionType[]>((acc, item, index) => {
    if (index === 0 && item.type === "divider") {
      return acc;
    }
    if (index === arr.length - 1 && item.type === "divider") {
      return acc;
    }
    if (
      item.type === "divider" &&
      acc.length > 0 &&
      acc[acc.length - 1].type === "divider"
    ) {
      return acc; // Skip consecutive dividers
    }
    acc.push(item);
    return acc;
  }, []);
};

const Dropdown = ({
  options,
  className = "",
  children,
  align = "start",
  size = ButtonSize.large,
  disabled,
  ...props
}: {
  children?: JSX.Element;
  "aria-label"?: string;
  align?: "start" | "end";
  className?: string;
  options: DropdownOptionType[];
  disabled?: boolean;
  size?: ButtonSize;
}) => {
  const link = useLink();
  const cleanedOptions = removeConsecutiveDividers(options);
  const handleStopPropagation = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleClickLink = (e: any) => {
    if (e.syntheticEvent && !e.syntheticEvent.metaKey) {
      e.syntheticEvent.preventDefault();
      e.syntheticEvent.stopPropagation();
      link.redirect(e.value);
    }
  };

  const renderOption = (option: DropdownOptionType, i: number) =>
    option.type === "header" ? (
      <MenuItem
        key={option.label}
        disabled
        className="text-xs text-gray-400 font-medium tracking-tight"
      >
        {titleCase(option.label)}
      </MenuItem>
    ) : option.type === "divider" ? (
      <MenuDivider key={i} />
    ) : option.type === "submenu" ? (
      <SubMenu key={`${i}-${option.label}`} label={option.label}>
        {option.options.map(renderOption)}
      </SubMenu>
    ) : option.type === "radio" ? (
      <Fragment key={`${i}-${option.label}`}>
        <MenuHeader>{option.label}</MenuHeader>
        <MenuRadioGroup
          value={option.value}
          onRadioChange={option.onChangeValue}
        >
          {option.options.map((subOption, subOptionIndex) => (
            <MenuItem
              disabled={subOption.disabled}
              key={String(subOption.value) || subOptionIndex}
              type="radio"
              value={subOption.value}
            >
              {titleCase(subOption.label)}
            </MenuItem>
          ))}
        </MenuRadioGroup>
      </Fragment>
    ) : option.type === "link" ? (
      <MenuItem
        key={i}
        disabled={option.disabled}
        value={option.to}
        onClick={handleClickLink}
        href={typeof option.to === "string" ? option.to : option.to.pathname}
        className={classNames(
          "block w-full text-left px-4 py-2 text-sm",
          "hover:bg-gray-100",
          option.textColor,
          !option.textColor && "text-gray-700 hover:text-gray-900",
          option.className
        )}
      >
        <div className={option.labelClassName}>{option.label}</div>
        {option.description && (
          <div className="text-gray-500 text-xs tracking-tight">
            {option.description}
          </div>
        )}
      </MenuItem>
    ) : (
      <MenuItem
        key={i}
        disabled={option.disabled}
        className={classNames(
          "block w-full text-left px-4 py-2 text-sm",
          "hover:bg-gray-100",
          option.textColor,
          !option.textColor && "text-gray-700 hover:text-gray-900",
          option.disabled && "opacity-50",
          option.className
        )}
        onClick={option.onClick}
      >
        <div className={option.labelClassName}>{option.label}</div>
        {option.description && (
          <div className="text-gray-500 text-xs tracking-tight">
            {option.description}
          </div>
        )}
      </MenuItem>
    );

  return (
    <Menu
      className="relative inline-block text-left text-sm not-prose divide-y z-dropdown tf-print-display-none max-w-72"
      portal
      align={align}
      menuButton={
        children ? (
          children
        ) : (
          <Button
            disabled={disabled}
            onClick={handleStopPropagation}
            icon={getTopicflowIcon("horizontalDots")}
            size={size}
            theme={buttonTheme.iconGray}
            aria-label={props["aria-label"]}
            className={classNames("shrink-0 text-gray-400 rounded", className)}
          />
        )
      }
    >
      {cleanedOptions.map(renderOption)}
    </Menu>
  );
};

export default Dropdown;
